import React, { useState } from "react";
import { Check, Copy, ExternalLink, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { BodyText, Card, CardContent, HeadingText } from "../../../shared/ui";
import { CustomerAvatar } from "./CustomerAvatar";
import { OtcRequestItem } from "../types";

interface OtcRequestOverviewCardProps {
  request: OtcRequestItem;
}

export const OtcRequestOverviewCard: React.FC<OtcRequestOverviewCardProps> = ({
  request,
}) => {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopiedKey(null), 1500);
  };

  const stateLower = request.state.toLowerCase();
  const isUnderReview = stateLower === "under review" || stateLower === "submitted";
  const isApproved = stateLower === "request approved" || stateLower === "approved";
  const isOfferSent = stateLower === "offer sent";
  const isCompleted = stateLower === "completed";

  return (
    <Card className="mt-5 overflow-hidden rounded-[16px] border border-[#EBEDF9] bg-white shadow-none">
      <CardContent className="p-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:divide-x lg:divide-[#EBEDF9]">
          {/* Column 1: Customer & Wallet / Settlement Details */}
          <div className="space-y-4 lg:pr-6">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <CustomerAvatar
                  name={request.customer.name}
                  pattern={request.customer.avatarPattern}
                  size={36}
                />
                <div>
                  <h3 className="aku-body-small-medium text-[#161827]">
                    {request.customer.name}
                  </h3>
                  <BodyText className="aku-body-small-regular text-[#6F739B]">
                    {request.customer.email}
                  </BodyText>
                </div>
              </div>

              <div className="flex items-center gap-2 text-[#6F739B]">
                <a
                  href={`tel:${request.customer.phone || "+234800000000"}`}
                  className="p-1 hover:text-[#161827] transition-colors"
                  title="Call customer"
                >
                  <Phone size={15} />
                </a>
                <a
                  href={`mailto:${request.customer.email}`}
                  className="p-1 hover:text-[#161827] transition-colors"
                  title="Email customer"
                >
                  <Mail size={15} />
                </a>
                <button
                  type="button"
                  onClick={() => toast.info("Opening customer profile...")}
                  className="p-1 text-[#6F00FF] hover:text-[#5800CC] transition-colors"
                  title="View customer profile"
                >
                  <ExternalLink size={15} />
                </button>
              </div>
            </div>

            {/* Wallet balance */}
            <div className="flex items-center justify-between pt-1">
              <span className="flex items-center gap-1.5 aku-body-small-regular text-[#6F739B]">
                <span className="text-[15px]">🇺🇸</span> Wallet balance
              </span>
              <span className="inline-flex items-center gap-1 aku-body-small-medium font-semibold text-[#161827]">
                {request.customer.walletBalance}
                <ExternalLink size={13} className="text-[#6F00FF]" />
              </span>
            </div>

            {/* Reference */}
            <div className="flex items-center justify-between">
              <span className="aku-body-small-regular text-[#6F739B]">Reference</span>
              <span className="inline-flex items-center gap-1 aku-body-small-medium text-[#161827]">
                {request.reference}
                <button
                  type="button"
                  onClick={() => handleCopy(request.reference, "ref")}
                  className="p-0.5 text-[#6F739B] hover:text-[#161827]"
                  title="Copy reference"
                >
                  {copiedKey === "ref" ? (
                    <Check size={13} className="text-emerald-600" />
                  ) : (
                    <Copy size={13} />
                  )}
                </button>
              </span>
            </div>

            {/* Settlement method */}
            <div className="flex items-center justify-between">
              <span className="aku-body-small-regular text-[#6F739B]">
                Settlement method
              </span>
              <span className="aku-body-small-medium text-[#161827]">
                {request.settlementMethod || "External"}
              </span>
            </div>

            {/* Recipient bank details */}
            <div className="flex items-start justify-between">
              <span className="aku-body-small-regular text-[#6F739B]">
                Recipient
              </span>
              <div className="text-right">
                <p className="aku-body-small-medium text-[#161827]">
                  {request.recipientDetails?.name || "-"}
                </p>
                <p className="aku-label-regular text-[#6F739B]">
                  {request.recipientDetails?.accountNumber
                    ? `${request.recipientDetails.accountNumber}${
                        request.recipientDetails.bankName
                          ? ` • ${request.recipientDetails.bankName}`
                          : ""
                      }`
                    : "-"}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="aku-body-small-regular text-[#6F739B]">
                Sort/SWIFT Code
              </span>
              <span className="aku-body-small-medium text-[#161827]">
                {request.recipientDetails?.swiftCode || "-"}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="aku-body-small-regular text-[#6F739B]">
                Routing number
              </span>
              <span className="aku-body-small-medium text-[#161827]">
                {request.recipientDetails?.routingNumber || "-"}
              </span>
            </div>

            {/* Date */}
            <div className="flex items-center justify-between">
              <span className="aku-body-small-regular text-[#6F739B]">Date</span>
              <span className="aku-body-small-medium text-[#161827]">
                {request.date}
              </span>
            </div>
          </div>

          {/* Column 2: Request details OR Offer details */}
          <div className="space-y-3.5 lg:px-6">
            <HeadingText className="aku-body-small-medium font-semibold text-[#161827]">
              {isUnderReview ? "Request details" : "Offer details"}
            </HeadingText>

            {isUnderReview ? (
              <>
                <div className="flex items-center justify-between">
                  <span className="aku-body-small-regular text-[#6F739B]">
                    Requested rate
                  </span>
                  <span className="aku-body-small-medium font-semibold text-[#161827]">
                    {request.requestedRate || "-"}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="aku-body-small-regular text-[#6F739B]">
                    Our rate
                  </span>
                  <span className="aku-body-small-medium font-semibold text-[#161827]">
                    {request.ourRate || "-"}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="aku-body-small-regular text-[#6F739B]">
                    Recipient to receive
                  </span>
                  <span className="aku-body-small-medium font-semibold text-[#161827]">
                    {request.recipientToReceive || "-"}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="aku-body-small-regular text-[#6F739B]">
                    Currency Pair
                  </span>
                  <span className="aku-body-small-medium text-[#161827]">
                    {request.pair || "USD - NGN"}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="aku-body-small-regular text-[#6F739B]">
                    Customer Source
                  </span>
                  <span className="aku-body-small-medium text-[#161827]">
                    {request.customerSource || "-"}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="aku-body-small-regular text-[#6F739B]">Notes</span>
                  <span className="aku-body-small-medium text-[#161827]">
                    {request.notes || "-"}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <span className="aku-body-small-regular text-[#6F739B]">
                    Rate offered
                  </span>
                  <span className="aku-body-small-medium font-semibold text-[#161827]">
                    {request.offeredRate || "-"}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="aku-body-small-regular text-[#6F739B]">
                    Recipient to receive
                  </span>
                  <span className="aku-body-small-medium font-semibold text-[#161827]">
                    {request.recipientToReceive || "-"}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="aku-body-small-regular text-[#6F739B]">
                    Expiring In
                  </span>
                  <span className="aku-body-small-medium text-[#6F00FF]">
                    {request.expiringIn || "-"}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="aku-body-small-regular text-[#6F739B]">
                    Requested rate
                  </span>
                  <span className="aku-body-small-medium text-[#161827]">
                    {request.requestedRate || "-"}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="aku-body-small-regular text-[#6F739B]">
                    Initial rate
                  </span>
                  <span className="aku-body-small-medium text-[#161827]">
                    {request.ourRate || "-"}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="aku-body-small-regular text-[#6F739B]">
                    Currency Pair
                  </span>
                  <span className="aku-body-small-medium text-[#161827]">
                    {request.pair || "USD - NGN"}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="aku-body-small-regular text-[#6F739B]">
                    Customer Source
                  </span>
                  <span className="aku-body-small-medium text-[#161827]">
                    {request.customerSource || "-"}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="aku-body-small-regular text-[#6F739B]">Notes</span>
                  <span className="aku-body-small-medium text-[#161827]">
                    {request.notes || "-"}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Column 3: Status timeline */}
          <div className="space-y-4 lg:pl-6">
            <HeadingText className="aku-body-small-medium font-semibold text-[#161827]">
              Status
            </HeadingText>

            <div className="relative space-y-5 pl-5 before:absolute before:bottom-2 before:left-[7px] before:top-2 before:w-0.5 before:bg-[#EBEDF9]">
              {(isCompleted || isApproved) && (
                <div className="relative">
                  <div className="absolute -left-5 top-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-emerald-500 ring-4 ring-emerald-50">
                    <div className="h-1.5 w-1.5 rounded-full bg-white" />
                  </div>
                  <p className="aku-body-small-medium text-[#161827]">
                    {isCompleted ? "Completed" : "Offer accepted"}
                  </p>
                  <p className="aku-label-regular text-[#6F739B]">
                    {request.timeline[2]?.timestamp || request.timeline[0]?.timestamp || request.date || "-"}
                  </p>
                </div>
              )}

              {(isApproved || isOfferSent) && (
                <div className="relative">
                  <div className="absolute -left-5 top-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-amber-500 ring-4 ring-amber-50">
                    <div className="h-1.5 w-1.5 rounded-full bg-white" />
                  </div>
                  <p className="aku-body-small-medium text-[#161827]">
                    Offer sent
                  </p>
                  <p className="aku-label-regular text-[#6F739B]">
                    {request.timeline[1]?.timestamp || request.timeline[0]?.timestamp || request.date || "-"}
                  </p>
                </div>
              )}

              <div className="relative">
                <div className="absolute -left-5 top-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[#6F00FF] ring-4 ring-purple-50">
                  <div className="h-1.5 w-1.5 rounded-full bg-white" />
                </div>
                <p className="aku-body-small-medium text-[#161827]">
                  Request submitted
                </p>
                <p className="aku-label-regular text-[#6F739B]">
                  {request.timeline[0]?.timestamp || request.date || "-"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
